import { useState, useCallback, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { LoadingScreen } from "@/components/layout/LoadingScreen";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Loader2, Package, Settings, Trash2 } from "lucide-react";

export function CombosTab({ products }) {
  const { authAxios } = useAuth();
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const [combos, setCombos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCombo, setEditingCombo] = useState(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [comboPrice, setComboPrice] = useState("");
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [imageUrl, setImageUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchCombos = useCallback(async () => {
    try { const res = await authAxiosRef.current.get("/menu/combos"); setCombos(res.data); }
    catch { }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchCombos(); }, [fetchCombos]);

  const resetForm = () => { setName(""); setDescription(""); setComboPrice(""); setSelectedProducts([]); setImageUrl(""); setEditingCombo(null); };
  const openCreate = () => { resetForm(); setShowForm(true); };
  const openEdit = (c) => {
    setEditingCombo(c); setName(c.name); setDescription(c.description || "");
    setComboPrice(c.combo_price || ""); setSelectedProducts(c.product_ids || []);
    setImageUrl(c.image_url || ""); setShowForm(true);
  };

  const toggleProduct = (pid) => setSelectedProducts(prev => prev.includes(pid) ? prev.filter(id => id !== pid) : [...prev, pid]);

  const originalTotal = selectedProducts.reduce((sum, pid) => {
    const p = products.find(pr => pr.id === pid);
    return sum + (p?.price || 0);
  }, 0);
  const savings = originalTotal - (parseFloat(comboPrice) || 0);

  const saveCombo = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Nombre obligatorio"); return; }
    if (selectedProducts.length < 2) { toast.error("Seleccione al menos 2 productos"); return; }
    setSubmitting(true);
    try {
      const payload = { name: name.trim(), description, combo_price: parseFloat(comboPrice) || 0, product_ids: selectedProducts, image_url: imageUrl, active: true };
      if (editingCombo) await authAxios.put(`/menu/combos/${editingCombo.id}`, payload);
      else await authAxios.post("/menu/combos", payload);
      toast.success(editingCombo ? "Combo actualizado" : "Combo creado");
      setShowForm(false); resetForm(); fetchCombos();
    } catch { toast.error("Error"); }
    setSubmitting(false);
  };

  const deleteCombo = async (id) => { try { await authAxios.delete(`/menu/combos/${id}`); toast.success("Eliminado"); fetchCombos(); } catch { toast.error("Error"); } };
  const getProductNames = (ids) => ids.map(id => products.find(p => p.id === id)?.name || "?").join(" + ");

  if (loading) return <LoadingScreen />;

  return (
    <div data-testid="combos-tab">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-gray-900">Combos / Menus del Dia</h3>
          <p className="text-sm text-gray-500">Cree paquetes de productos con precio especial</p>
        </div>
        <Button className="bg-emerald-600 hover:bg-emerald-700 gap-1" onClick={openCreate} data-testid="add-combo-btn">
          <Plus className="w-4 h-4" /> Nuevo Combo
        </Button>
      </div>

      {combos.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-lg font-medium">Sin combos</p>
          <p className="text-sm">Cree combos como "Menu del Dia" o "Pack Familia"</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {combos.map(c => {
            const origPrice = (c.product_ids || []).reduce((s, pid) => s + (products.find(p => p.id === pid)?.price || 0), 0);
            return (
              <div key={c.id} className="bg-white rounded-lg border border-gray-200 p-4 relative" data-testid={`combo-card-${c.id.slice(-6)}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="font-bold text-gray-900">{c.name}</h4>
                    {c.description && <p className="text-xs text-gray-500 mt-0.5">{c.description}</p>}
                    <p className="text-sm text-gray-600 mt-1">{getProductNames(c.product_ids || [])}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-xs text-gray-400 line-through">{origPrice.toFixed(2)}EUR</span>
                      <span className="text-lg font-bold text-emerald-700">{(c.combo_price || 0).toFixed(2)}EUR</span>
                      {origPrice > c.combo_price && <span className="text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full font-bold">-{(origPrice - c.combo_price).toFixed(2)}EUR</span>}
                    </div>
                  </div>
                  {c.image_url && <img src={c.image_url} alt="" className="w-16 h-16 rounded-lg object-cover ml-3" />}
                </div>
                <div className="flex gap-1 mt-2">
                  <Button variant="ghost" size="sm" className="text-gray-500 h-7" onClick={() => openEdit(c)}><Settings className="w-3.5 h-3.5 mr-1" /> Editar</Button>
                  <Button variant="ghost" size="sm" className="text-red-400 h-7" onClick={() => deleteCombo(c.id)}><Trash2 className="w-3.5 h-3.5 mr-1" /> Eliminar</Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={(o) => { if (!o) { setShowForm(false); resetForm(); } else setShowForm(true); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="uppercase font-bold">{editingCombo ? "Editar" : "Nuevo"} Combo</DialogTitle></DialogHeader>
          <form onSubmit={saveCombo} className="space-y-4 mt-2">
            <div><Label>Nombre del Combo *</Label><Input value={name} onChange={e => setName(e.target.value)} placeholder='Ej: "Menu del Dia" o "Pack Familia"' className="mt-1" data-testid="combo-name" /></div>
            <div><Label>Descripcion</Label><Input value={description} onChange={e => setDescription(e.target.value)} placeholder="Descripcion opcional" className="mt-1" /></div>
            <div>
              <Label>Productos del Combo * <span className="text-gray-400 text-xs font-normal">(min. 2)</span></Label>
              <div className="mt-1 max-h-40 overflow-auto border rounded-lg p-2 space-y-1">
                {products.filter(p => p.for_sale).map(p => (
                  <label key={p.id} className={`flex items-center justify-between gap-2 text-sm cursor-pointer p-1.5 rounded transition ${selectedProducts.includes(p.id) ? "bg-emerald-50" : "hover:bg-gray-50"}`}>
                    <div className="flex items-center gap-2">
                      <input type="checkbox" checked={selectedProducts.includes(p.id)} onChange={() => toggleProduct(p.id)} className="accent-emerald-600" />
                      <span>{p.name}</span>
                    </div>
                    <span className="text-gray-500 text-xs">{p.price.toFixed(2)}EUR</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label>Precio del Combo *</Label>
              <div className="flex items-center gap-3 mt-1">
                <Input type="number" step="0.01" value={comboPrice} onChange={e => setComboPrice(e.target.value)} placeholder="0.00" className="w-32" data-testid="combo-price" />
                <span className="text-sm text-gray-500">EUR</span>
                {selectedProducts.length >= 2 && (
                  <div className="text-xs text-gray-500">
                    Original: <span className="line-through">{originalTotal.toFixed(2)}EUR</span>
                    {savings > 0 && <span className="text-red-600 font-bold ml-1">Ahorro: {savings.toFixed(2)}EUR</span>}
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setShowForm(false); resetForm(); }}>Cancelar</Button>
              <Button type="submit" disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-combo-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : editingCombo ? "Actualizar" : "Crear Combo"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
